import {
  apiErrorMessage,
  box,
  button,
  container,
  loader,
  loadingButton,
  logo,
  message,
  pageSubtitle,
} from '@/ui/components';
import { computed, h, defineComponent, ref, watch, getCurrentInstance } from 'vue';
import { inputField } from '@/ui/components/form';
import { checkPasswordReset, executePasswordSet } from '@/ui/auth/auth';
import { locales, t } from '@/ui/i18n/i18n';
import { useSetLocale, useTask, useValidation } from '@/ui/concerns';
import { PasswordResetCheckingUser, PasswordResettingUser, validate } from '@/entities/auth/passwordResettingUser';
import { Route } from 'vue-router';
import { StatusCodes } from 'http-status-codes';
import { appStoreLink, isMobile, pushRoute } from '@/ui/helpers';

interface LocalePageProps {
  locale?: locales;
}

const getToken = (route: Route): string => {
  let { token } = route.query;
  if (typeof (token) !== 'string') {
    token = '';
  }
  return token;
};

export const SetPasswordPage = defineComponent<LocalePageProps>({
  name: 'SetPasswordPage',
  props: ['locale'],
  setup(props) {
    useSetLocale(props.locale);

    const token = computed(() => {
      const route = getCurrentInstance()?.proxy?.$router?.currentRoute;

      if (!route) {
        return '';
      }

      return getToken(route);
    });

    const passwordSettingUser = ref<PasswordResetCheckingUser>({
      id: 0,
      name: '',
      email: '',
      organization: '',
      expiresAt: new Date(),
    });

    const user = ref<PasswordResettingUser>({ password: '' });

    const checkToken = useTask(
      () => checkPasswordReset(token.value),
      checkingUser => {
        passwordSettingUser.value = checkingUser;
      },
    );
    watch(token, checkToken.run);

    const {
      run: submitForm,
      state,
    } = useTask(
      () => executePasswordSet({
        token: token.value,
        user: user.value,
      }),
      () => {
        if (isMobile()) {
          // @todo: add deeplink here if we go with that direction
          window.open(appStoreLink());

          return;
        }

        pushRoute({ name: 'home' });
      },
    );

    const {
      validationErrors,
      hasNoValidationErrors,
      showValidationErrors,
    } = useValidation(validate, user, state);

    const handleReset = () => {
      showValidationErrors.value = true;
      if (hasNoValidationErrors.value) {
        submitForm();
      }
    };

    return function render() {
      const invalidTokenError = message('error', [
        pageSubtitle(t('auth.token-invalid')),
      ]);
      const expiredTokenError = message('error', [
        pageSubtitle(t('auth.token-expired')),
      ]);
      const tokenCheckError = message('error', [
        pageSubtitle(t('auth.token-failed')),
        button(t('common.retry'), checkToken.run),
      ]);

      const form = [
        pageSubtitle(t('auth.welcome', { name: passwordSettingUser.value.name })),
        h('div', { class: 'content' }, [
          h(
            'p',
            t('auth.choose-password'),
          ),
        ]),
        inputField({
          name: 'password',
          type: 'password',
          label: t('auth.password-new'),
          placeholder: t('auth.enter-new-password'),
          value: user.value.password,
          required: true,
          errors: validationErrors.value,
          disabled: state.value.pending,
        }, v => {
          user.value.password = v;
        }),
        apiErrorMessage(state.value),
        loadingButton(t('auth.set-and-sign'), handleReset, state.value.pending, { expanded: true }),
      ];

      const resetForm = [
        loader(
          'div',
          checkToken,
          [
            h(
              'template',
              { slot: 'error' },
              'error' in checkToken.state.value && checkToken.state.value.error.code === StatusCodes.UNAUTHORIZED
                ? [expiredTokenError]
                : [tokenCheckError],
            ),
            form,
          ],
          false,
        ),
      ];

      return container(
        [
          logo(),
          box(token.value !== '' ? resetForm : [invalidTokenError]),
        ],
        'small',
      );
    };
  },
});
